import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Award, MoreHorizontal, Trash2, AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface Certification {
  id: string
  userId: string
  name: string
  vendor: string | null
  certNumber: string | null
  earnedDate: string | null
  expiryDate: string | null
  cpdHours: number
  status: string
  tags: string[]
}

function AddCertDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [vendor, setVendor] = useState('')
  const [certNumber, setCertNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/training/certifications', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certifications'] })
      toast({ title: 'Certification added' })
      setOpen(false)
      setName(''); setVendor(''); setCertNumber(''); setExpiryDate('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add certification', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" /> Add Certification</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Certification</DialogTitle><DialogDescription>Track a new certification or qualification.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2"><Label>Certification Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="AZ-104 Azure Administrator" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Vendor</Label><Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Microsoft" /></div>
            <div className="space-y-2"><Label>Cert Number</Label><Input value={certNumber} onChange={(e) => setCertNumber(e.target.value)} placeholder="ABC-12345" /></div>
          </div>
          <div className="space-y-2"><Label>Expiry Date</Label><Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate({ name, vendor: vendor || undefined, certNumber: certNumber || undefined, expiryDate: expiryDate || undefined })} disabled={!name || createMutation.isPending}>
            {createMutation.isPending ? 'Adding...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function TrainingPage() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['certifications'],
    queryFn: () => apiClient.get<{ certifications: Certification[]; pagination: unknown }>('/training/certifications'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/training/certifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certifications'] })
      toast({ title: 'Deleted' })
    },
  })

  const certs = data?.data?.certifications || []
  const filtered = certs.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.vendor?.toLowerCase().includes(search.toLowerCase()))

  const expiringCount = certs.filter((c) => {
    if (!c.expiryDate) return false
    const days = Math.ceil((new Date(c.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return days <= 90 && days > 0
  }).length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Training & Certifications</h1>
          <p className="text-muted-foreground">Track certifications, CPD hours, and skills</p>
        </div>
        <AddCertDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{certs.length}</div><p className="text-xs text-muted-foreground">Total Certifications</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{certs.filter(c => c.status === 'active').length}</div><p className="text-xs text-muted-foreground">Active</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-yellow-500">{expiringCount}</div><p className="text-xs text-muted-foreground">Expiring (90 days)</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search certifications..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /></CardContent></Card>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-12"><Award className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" /><p className="text-lg font-medium text-muted-foreground">No certifications found</p></CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((cert) => {
            const daysUntilExpiry = cert.expiryDate ? Math.ceil((new Date(cert.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null
            return (
              <Card key={cert.id}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-base">{cert.name}</CardTitle>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(cert.id)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {cert.vendor && <Badge variant="outline">{cert.vendor}</Badge>}
                    <Badge variant={cert.status === 'active' ? 'default' : cert.status === 'expired' ? 'destructive' : 'secondary'}>{cert.status}</Badge>
                    {daysUntilExpiry !== null && daysUntilExpiry <= 90 && daysUntilExpiry > 0 && (
                      <Badge variant={daysUntilExpiry <= 30 ? 'destructive' : 'outline'} className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> {daysUntilExpiry}d
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {cert.certNumber && <p>#{cert.certNumber}</p>}
                    {cert.expiryDate && <p>Expires: {new Date(cert.expiryDate).toLocaleDateString()}</p>}
                    {cert.cpdHours > 0 && <p>CPD: {cert.cpdHours} hours</p>}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
